import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { Badge, FleetStatusBadge, QualityBar } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardBody, EmptyState, Spinner } from '../../components/Card';
import { Select, TextInput } from '../../components/Field';
import { DataTable, type Column } from '../../components/Table';
import { useOverlay } from '../../overlay';
import { toast } from '../../components/Toast';
import { api, errorMessage } from '../../lib/api';
import { batteryTone, type FleetStatus } from '../../lib/droneStatus';
import { useAuthStore } from '../../app/authStore';
import { useFleetLinks, useLiveTick } from '../../drone/hooks';
import { formatRelativeTime } from '@shared/format';
import type { Drone, DroneCredentialMode } from '@shared/types';
import { useFleetStore } from './fleetStore';
import { droneFleetStatus } from './status';
import { DroneFormModal } from './components/DroneFormModal';
import { BatchConfigModal } from './components/BatchConfigModal';
import { GroupManager } from './components/GroupManager';
import { LinkQualityText } from './components/LinkQualityText';

/**
 * 本地 Mock 机队注册表地址（`platform/mock-drone-server` 提供）。
 *
 * 只在开发环境或显式配置 `VITE_MOCK_REGISTRY_URL` 时可用：生产环境不应让访客
 * 的浏览器去访问 127.0.0.1，导入按钮会整体隐藏。
 */
const MOCK_REGISTRY_URL =
  import.meta.env.VITE_MOCK_REGISTRY_URL ??
  (import.meta.env.DEV ? 'http://127.0.0.1:9100/registry.json' : '');

const CREDENTIAL_MODE_LABELS: Record<DroneCredentialMode, string> = {
  platformToken: '平台令牌',
  apiKey: 'API Key',
  none: '无鉴权',
};

interface MockRegistryEntry {
  id?: string;
  name?: string;
  uniqueId?: string;
  model?: string;
  vendor?: string;
  flavor?: string;
  protocol?: string;
  host?: string;
  port?: number;
  basePath?: string;
  wsPath?: string;
  credential?: string;
  credentialMode?: string;
  adapterProfile?: string;
  tags?: string[];
  groupId?: string | null;
  /** 兼容嵌套写法：部分版本/工具的注册表把接入信息放在 endpoint 下 */
  endpoint?: {
    protocol?: string;
    host?: string;
    port?: number;
    basePath?: string;
    wsPath?: string;
  };
}

export function DroneRegistryPage() {
  const navigate = useNavigate();
  const overlay = useOverlay();
  const { drones, groups, loading, loaded, error, load, applyDrone, applyDrones, removeDrones } =
    useFleetStore();
  const canWrite = useAuthStore((state) => state.permissions.includes('fleet.registry.write'));
  const entries = useFleetLinks(drones);
  const now = useLiveTick(1_000);

  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Drone | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return drones.filter((drone) => {
      if (groupFilter === '__none__') {
        if (drone.groupId !== null) return false;
      } else if (groupFilter !== '' && drone.groupId !== groupFilter) {
        return false;
      }
      if (statusFilter !== '') {
        if (statusFilter === 'enabled') {
          if (!drone.enabled) return false;
        } else if (statusFilter === 'disabled') {
          if (drone.enabled) return false;
        } else if (droneFleetStatus(drone, entries[drone.id], now) !== (statusFilter as FleetStatus)) {
          return false;
        }
      }
      if (keyword === '') return true;
      return (
        drone.name.toLowerCase().includes(keyword) ||
        drone.uniqueId.toLowerCase().includes(keyword) ||
        drone.model.toLowerCase().includes(keyword) ||
        drone.vendor.toLowerCase().includes(keyword) ||
        drone.endpoint.host.toLowerCase().includes(keyword) ||
        drone.tags.some((tag) => tag.toLowerCase().includes(keyword))
      );
    });
  }, [drones, entries, now, search, groupFilter, statusFilter]);

  const selectedDrones = useMemo(
    () => drones.filter((drone) => selected.has(drone.id)),
    [drones, selected],
  );

  async function removeDrone(event: ReactMouseEvent<HTMLButtonElement>, drone: Drone) {
    const confirmed = await overlay.confirm({
      placementKey: `drone-delete:${drone.id}`,
      trigger: event.currentTarget,
      tone: 'danger',
      title: `删除无人机「${drone.name}」？`,
      description: '删除后该设备将从注册表移除，其接入凭据一并清除。历史审计记录会保留。',
      confirmText: '删除',
    });
    if (!confirmed) return;
    try {
      await api.delete(`/api/drones/${drone.id}`);
      removeDrones([drone.id]);
      setSelected((current) => {
        const next = new Set(current);
        next.delete(drone.id);
        return next;
      });
      toast.ok('无人机已删除');
    } catch (cause) {
      toast.critical(errorMessage(cause));
    }
  }

  async function batchDelete(event: ReactMouseEvent<HTMLButtonElement>) {
    const confirmed = await overlay.confirm({
      placementKey: 'drone-batch-delete',
      trigger: event.currentTarget,
      tone: 'danger',
      title: `删除选中的 ${selected.size} 台无人机？`,
      description: '此操作不可撤销。删除后对应设备的接入配置与凭据都会被清除。',
      confirmText: `删除 ${selected.size} 台`,
    });
    if (!confirmed) return;
    try {
      const ids = Array.from(selected);
      await api.post('/api/drones/batch/delete', { ids });
      removeDrones(ids);
      setSelected(new Set());
      toast.ok(`已删除 ${ids.length} 台无人机`);
    } catch (cause) {
      toast.critical(errorMessage(cause));
    }
  }

  async function setEnabled(value: boolean) {
    const ids = Array.from(selected);
    try {
      const result = await api.post<{ updated: Drone[] }>('/api/drones/batch', { ids, enabled: value });
      applyDrones(result.updated);
      toast.ok(`已${value ? '启用' : '停用'} ${result.updated.length} 台无人机`);
    } catch (cause) {
      toast.critical(errorMessage(cause));
    }
  }

  /**
   * 一键导入本地 Mock 机队。
   *
   * 只读取开发用的注册表文件，逐台调用正式注册接口，
   * 因此导入结果与手工注册完全一致（同样走校验与审计）。
   */
  async function importMockFleet() {
    setImporting(true);
    try {
      const response = await fetch(MOCK_REGISTRY_URL, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Mock 注册表返回 ${response.status}`);
      const payload = (await response.json()) as { drones?: MockRegistryEntry[] } | MockRegistryEntry[];
      const list = Array.isArray(payload) ? payload : (payload.drones ?? []);
      if (list.length === 0) throw new Error('Mock 注册表里没有无人机条目');

      const failures: string[] = [];
      let created = 0;
      for (const [index, entry] of list.entries()) {
        const uniqueId = entry.uniqueId ?? `MOCK-${String(index + 1).padStart(3, '0')}`;
        const nested = entry.endpoint ?? {};
        try {
          const drone = await api.post<Drone>('/api/drones', {
            name: entry.name ?? uniqueId,
            uniqueId,
            model: entry.model ?? 'Mock-Sim',
            vendor: entry.vendor ?? 'Havorix Mock',
            endpoint: {
              protocol: entry.protocol ?? nested.protocol ?? 'http',
              host: entry.host ?? nested.host ?? '127.0.0.1',
              port: entry.port ?? nested.port ?? 9201 + index,
              basePath: entry.basePath ?? nested.basePath ?? '/api/v1',
              wsPath: entry.wsPath ?? nested.wsPath ?? '/api/v1/telemetry/stream',
            },
            credentialMode: entry.credentialMode ?? 'apiKey',
            credential: entry.credential ?? `havorix-mock-key-${index + 1}`,
            adapterProfile: entry.adapterProfile ?? entry.flavor,
            tags: entry.tags ?? ['mock'],
            groupId: entry.groupId ?? null,
            enabled: true,
          });
          applyDrone(drone);
          created += 1;
        } catch (cause) {
          failures.push(`${uniqueId}：${errorMessage(cause)}`);
        }
      }

      if (created > 0) toast.ok(`已导入 ${created} 台 Mock 无人机`);
      if (failures.length > 0) {
        await overlay.alert({
          placementKey: 'mock-import-failures',
          trigger: null,
          title: `${failures.length} 台 Mock 无人机导入失败`,
          description: (
            <ul className="flex list-disc flex-col gap-0.5 pl-4">
              {failures.slice(0, 6).map((message) => (
                <li key={message}>{message}</li>
              ))}
              {failures.length > 6 ? <li>…其余 {failures.length - 6} 条已省略</li> : null}
            </ul>
          ),
        });
      }
      if (created === 0 && failures.length === 0) toast.caution('没有可导入的设备');
    } catch (cause) {
      toast.critical(
        `无法读取本地 Mock 注册表：${errorMessage(cause)}。请确认已运行 npm run dev:drones。`,
      );
    } finally {
      setImporting(false);
    }
  }

  const columns: Column<Drone>[] = [
    {
      key: 'name',
      header: '设备',
      render: (drone) => {
        const group = groups.find((item) => item.id === drone.groupId);
        return (
          <div className="flex items-center gap-2">
            <span
              aria-hidden
              className="size-2.5 shrink-0 rounded-full"
              style={{ background: group?.color ?? 'var(--line-strong)' }}
            />
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium text-fg">{drone.name}</span>
              <span className="tabular truncate font-mono text-[11px] text-fg-subtle">{drone.uniqueId}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: '状态',
      render: (drone) => (
        <div className="flex items-center gap-2">
          <FleetStatusBadge status={droneFleetStatus(drone, entries[drone.id], now)} />
          {drone.enabled ? null : <Badge tone="neutral">已停用</Badge>}
        </div>
      ),
    },
    {
      key: 'link',
      header: '信号',
      width: '90px',
      hideOnMobile: true,
      render: (drone) => {
        const telemetry = entries[drone.id]?.telemetry;
        if (!telemetry) return <span className="text-xs text-fg-subtle">--</span>;
        return (
          <LinkQualityText
            value={telemetry.link.quality}
            frame={telemetry.seq}
            className="text-xs"
          />
        );
      },
    },
    {
      key: 'battery',
      header: '电量',
      width: '130px',
      hideOnMobile: true,
      render: (drone) => {
        const telemetry = entries[drone.id]?.telemetry;
        if (!telemetry) return <span className="text-xs text-fg-subtle">--</span>;
        const tone = batteryTone(telemetry.battery.percent);
        return (
          <div className="flex flex-col gap-1">
            <span className="tabular text-xs font-medium" style={{ color: `var(--${tone})` }}>
              {telemetry.battery.percent.toFixed(0)}%
            </span>
            <QualityBar value={telemetry.battery.percent} tone={tone} ariaLabel="剩余电量" />
          </div>
        );
      },
    },
    {
      key: 'endpoint',
      header: '接入地址',
      hideOnMobile: true,
      render: (drone) => (
        <div className="flex flex-col">
          <span className="tabular font-mono text-[11px] text-fg">
            {drone.endpoint.protocol}://{drone.endpoint.host}:{drone.endpoint.port}
          </span>
          <span className="font-mono text-[11px] text-fg-subtle">{drone.endpoint.basePath}</span>
        </div>
      ),
    },
    {
      key: 'auth',
      header: '鉴权',
      width: '150px',
      hideOnMobile: true,
      render: (drone) => (
        <div className="flex flex-col gap-0.5">
          <Badge tone={drone.credentialMode === 'none' ? 'caution' : 'neutral'}>
            {CREDENTIAL_MODE_LABELS[drone.credentialMode]}
          </Badge>
          <span className="text-[11px] text-fg-subtle">
            {drone.hasCredential ? '已配置凭据' : '未配置凭据'}
          </span>
        </div>
      ),
    },
    {
      key: 'rate',
      header: '遥测 / 失联',
      hideOnMobile: true,
      render: (drone) => (
        <span className="tabular text-[11px] text-fg-muted">
          {drone.telemetryRateHz} Hz / {(drone.staleAfterMs / 1000).toFixed(1)} s
        </span>
      ),
    },
    {
      key: 'seen',
      header: '最近在线',
      hideOnMobile: true,
      render: (drone) => (
        <span className="text-[11px] text-fg-muted">
          {formatRelativeTime(drone.lastSeenAt ? Date.parse(drone.lastSeenAt) : null, now)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '操作',
      align: 'right',
      render: (drone) => (
        <div className="flex items-center justify-end gap-1.5">
          <Button size="sm" variant="ghost" onClick={() => navigate(`/fleet/tracking/${drone.id}`)}>
            监管
          </Button>
          {canWrite ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditing(drone);
                  setFormOpen(true);
                }}
              >
                编辑
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={(event) => void removeDrone(event, drone)}
              >
                删除
              </Button>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  if (!loaded && loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="flex flex-col gap-3 p-3 lg:p-4">
        {error ? (
          <Card tone="critical">
            <CardBody className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-fg">注册表加载失败：{error}</span>
              <Button size="sm" className="ml-auto" onClick={() => void load({ force: true })}>
                重试
              </Button>
            </CardBody>
          </Card>
        ) : null}

        <Card>
          <CardBody className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
              <TextInput
                label="搜索"
                placeholder="名称 / 唯一 ID / 型号 / 地址 / 标签"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <Select
                label="分组"
                value={groupFilter}
                options={[
                  { value: '', label: '全部分组' },
                  { value: '__none__', label: '未分组' },
                  ...groups.map((group) => ({ value: group.id, label: group.name })),
                ]}
                onChange={(event) => setGroupFilter(event.target.value)}
              />
              <Select
                label="状态"
                value={statusFilter}
                options={[
                  { value: '', label: '全部状态' },
                  { value: 'enabled', label: '已启用' },
                  { value: 'disabled', label: '已停用' },
                  { value: 'flying', label: '飞行中' },
                  { value: 'idle', label: '待命' },
                  { value: 'offline', label: '离线' },
                  { value: 'fault', label: '告警' },
                ]}
                onChange={(event) => setStatusFilter(event.target.value)}
              />
              <div className="flex items-end gap-1.5">
                <Button
                  variant="primary"
                  disabled={!canWrite}
                  onClick={() => {
                    setEditing(null);
                    setFormOpen(true);
                  }}
                >
                  注册无人机
                </Button>
                {MOCK_REGISTRY_URL ? (
                  <Button
                    loading={importing}
                    disabled={!canWrite}
                    title={`从 ${MOCK_REGISTRY_URL} 读取并逐台注册`}
                    onClick={() => void importMockFleet()}
                  >
                    导入 Mock 机队
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-line pt-2.5">
              <span className="text-xs text-fg-muted">
                共 {rows.length} 台{rows.length !== drones.length ? `（已筛选，全部 ${drones.length} 台）` : ''}
              </span>
              {!canWrite ? <Badge tone="info">当前角色为只读</Badge> : null}
              <div className="ml-auto flex items-center gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => void load({ force: true })} loading={loading}>
                  刷新
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelected(new Set(rows.map((drone) => drone.id)))}
                  disabled={rows.length === 0}
                >
                  全选当前筛选
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        {selected.size > 0 ? (
          <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 rounded-lg border border-accent/50 bg-surface-1 px-3 py-2.5">
            <Badge tone="info" solid>
              已选择 {selected.size} 台
            </Badge>
            <div className="ml-auto flex flex-wrap items-center gap-1.5">
              <Button size="sm" disabled={!canWrite} onClick={() => setBatchOpen(true)}>
                统一配置
              </Button>
              <Button size="sm" disabled={!canWrite} onClick={() => void setEnabled(true)}>
                批量启用
              </Button>
              <Button size="sm" disabled={!canWrite} onClick={() => void setEnabled(false)}>
                批量停用
              </Button>
              <Button size="sm" variant="danger" disabled={!canWrite} onClick={(event) => void batchDelete(event)}>
                批量删除
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                清除选择
              </Button>
            </div>
          </div>
        ) : null}

        <DataTable
          rows={rows}
          columns={columns}
          getRowId={(drone) => drone.id}
          loading={loading}
          selectedIds={selected}
          onSelectedChange={setSelected}
          empty={
            drones.length === 0 ? (
              <EmptyState
                title="注册表为空"
                description="注册第一台无人机，或一键导入本地 Mock 机队进行联调。"
                action={
                  canWrite ? (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => {
                        setEditing(null);
                        setFormOpen(true);
                      }}
                    >
                      注册无人机
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <EmptyState title="没有符合条件的设备" description="试着调整搜索关键词或筛选条件。" />
            )
          }
        />

        <GroupManager />
      </div>

      <DroneFormModal
        open={formOpen}
        drone={editing}
        groups={groups}
        onClose={() => setFormOpen(false)}
        onSaved={({ drone }) => applyDrone(drone)}
      />

      <BatchConfigModal
        open={batchOpen}
        drones={selectedDrones}
        groups={groups}
        onClose={() => setBatchOpen(false)}
        onApplied={(updated) => {
          applyDrones(updated);
          setSelected(new Set());
        }}
      />
    </div>
  );
}
